import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { useDooFeeCandidates } from "../hooks";
import type { DooFeeCandidate } from "../api";

export type DooModule = "wypis" | "mz" | "mpzp";

const FEE_STATUS_LABELS: Record<string, string> = {
  pending_approval: "oczekuje",
  approved: "zaakceptowana",
  payment_uploaded: "opłacona",
  acknowledged: "odbiór potwierdzony",
};

const candidateLabel = (c: DooFeeCandidate) =>
  [
    c.office_name,
    `${c.amount} zł`,
    c.transfer_name,
    FEE_STATUS_LABELS[c.status] ?? c.status,
    c.project_name,
  ]
    .filter(Boolean)
    .join(" · ");

/**
 * Opcjonalne pole modali DOO: zamiast tworzyć nową prośbę w /official-fees można
 * podpiąć jedną z WŁASNYCH istniejących opłat (żywych, niepodpiętych pod inne
 * zamówienie). Puste = domyślna ścieżka „utwórz nową". Gdy użytkownik nie ma żadnych
 * kandydatek, pole się nie renderuje — modal wygląda jak dotychczas.
 */
export function AttachExistingFeeField({
  module,
  value,
  onChange,
}: {
  module: DooModule;
  value: string;
  onChange: (feeId: string) => void;
}) {
  const { data: candidates } = useDooFeeCandidates(module);
  if (!candidates?.length) return null;
  return (
    <label className="block">
      <span className="section-label">Opłata urzędowa — podepnij istniejącą (opcjonalnie)</span>
      <select
        className="input-base mt-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">— utwórz nową opłatę —</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {candidateLabel(c)}
          </option>
        ))}
      </select>
      <span className="block mt-1 text-[11px] text-[var(--color-text-muted)]">
        Tylko Twoje opłaty z /official-fees, niepodpięte pod inne zamówienie. Wybór
        istniejącej pomija formularz nowej opłaty.
      </span>
    </label>
  );
}

const DETACH = "__detach__";

/**
 * Edycja DOO na stronie zamówienia (wypisy / MZ / MPZP): podmiana pliku PDF i/lub
 * przepięcie opłaty urzędowej na inną własną (albo odpięcie). Nową opłatę tworzy się
 * przyciskiem „Dodaj opłatę / Złóż ponownie" (onRequestNew), bo tam działa pełny
 * formularz z odczytem DOO przez AI.
 */
export function EditDooModal({
  module,
  orderNumber,
  currentFee,
  currentDooFilename,
  pending,
  onClose,
  onSubmit,
  onRequestNew,
}: {
  module: DooModule;
  orderNumber: string;
  currentFee: { id: string; amount: string | null; status: string | null } | null;
  currentDooFilename?: string | null;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payload: { fee?: string; file?: File }) => void;
  /** Otwiera modal „Dodaj opłatę" (tworzenie nowej) — pokazywane tylko, gdy podane. */
  onRequestNew?: () => void;
}) {
  const { data: candidates } = useDooFeeCandidates(module);
  const currentValue = currentFee?.id ?? "";
  const [selected, setSelected] = useState(currentValue);
  const [files, setFiles] = useState<File[]>([]);

  const feeChanged = selected !== currentValue;
  const hasChange = feeChanged || files.length > 0;

  const submit = () => {
    const payload: { fee?: string; file?: File } = {};
    if (feeChanged) payload.fee = selected === DETACH ? "" : selected;
    if (files[0]) payload.file = files[0];
    onSubmit(payload);
  };

  return (
    <Modal open onClose={onClose} title={`Edycja DOO — ${orderNumber}`}>
      <div className="space-y-3 text-xs">
        <div className="space-y-1">
          <span className="section-label">
            Plik DOO {currentDooFilename ? `(obecnie: ${currentDooFilename})` : "(brak)"}
          </span>
          <FileDropZone files={files} accept=".pdf" onFilesChange={setFiles} />
          <span className="block text-[11px] text-[var(--color-text-muted)]">
            Wgranie pliku podmienia dotychczasowy dokument. Kwoty podpiętej opłaty to nie
            zmienia — opłatę edytuje się w /official-fees.
          </span>
        </div>

        <label className="block">
          <span className="section-label">Podpięta opłata urzędowa</span>
          <select
            className="input-base mt-1"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            {currentFee ? (
              <option value={currentFee.id}>
                obecna · {currentFee.amount ? `${currentFee.amount} zł · ` : ""}
                {FEE_STATUS_LABELS[currentFee.status ?? ""] ?? currentFee.status ?? ""}
              </option>
            ) : (
              <option value="">— brak podpiętej opłaty —</option>
            )}
            {(candidates ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {candidateLabel(c)}
              </option>
            ))}
            {currentFee && <option value={DETACH}>— odepnij opłatę —</option>}
          </select>
          <span className="block mt-1 text-[11px] text-[var(--color-text-muted)]">
            Do wyboru tylko Twoje opłaty z /official-fees, niepodpięte pod inne zamówienie.
            Odpięta opłata nie znika — zostaje w /official-fees.
          </span>
        </label>

        {onRequestNew && (
          <button
            type="button"
            className="text-[var(--color-primary)] hover:underline"
            onClick={onRequestNew}
          >
            ➕ Utwórz nową opłatę (formularz z odczytem DOO)
          </button>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Anuluj
          </Button>
          <Button size="sm" loading={pending} disabled={!hasChange} onClick={submit}>
            Zapisz zmiany
          </Button>
        </div>
      </div>
    </Modal>
  );
}
